import React from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { compose } from "recompose";
import { Action } from "typescript-fsa";
import { Pie } from "react-chartjs-2";
import { ChartData, ChartOptions, TooltipItem } from "chart.js";

import { makeDemographicsDownloadGet } from "@api/index";
import { DownloadCsvButton } from "@ui/DownloadCsvButton";
import { getChartLegendOptions, getChartTooltipOptions } from "../../constants";
import {
    MusicIdProps,
    makeGetGenderChartsData,
    getSettingFrom,
    getSettingTo,
    IntlProps,
} from "../../selectors";
import { SalesState } from "../../models";

interface GenderPieChartStateProps {
    data: ChartData<"pie">;
    downloadAction: () => Action<any>;
}

type Props = GenderPieChartStateProps & MusicIdProps & IntlProps;

const GenderPieChart = ({ data, downloadAction, intl: { formatNumber } }: Props) => {
    const options: ChartOptions<"pie"> = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1,
        layout: {
            padding: {
                top: 0,
                bottom: 48,
                left: 0,
                right: 0,
            },
        },
        plugins: {
            legend: {
                ...getChartLegendOptions(),
            },
            tooltip: {
                ...getChartTooltipOptions(),
                callbacks: {
                    label: ({ label, dataset, dataIndex }: TooltipItem<"pie">) => {
                        label = label || "";
                        if (label) {
                            label = " " + label + ": ";
                        }
                        // Format the number
                        let total = 0;
                        for (const gVal of dataset.data) {
                            total += gVal as number;
                        }
                        const val = Number(dataset.data[dataIndex]);
                        label += formatNumber(val);
                        // Add a percentage
                        label += " (" + formatNumber((val / total), {
                            style: "percent",
                            maximumFractionDigits: 2,
                        }) + ")";
                        return label;
                    },
                },
            },
        },
    };

    return (
        <div className="gender-pie-chart">
            <div className="gender-pie-chart__chart">
                <Pie data={data} options={options} height={null} width={null} />
            </div>
            <DownloadCsvButton actionCreator={downloadAction} />
        </div>
    );
};

const makeMapStateToProps = () => {
    const getGenderChartsData = makeGetGenderChartsData();
    return (state: SalesState, props: MusicIdProps) => {
        const from = getSettingFrom(state);
        const to = getSettingTo(state);
        return {
            data: getGenderChartsData(state, props),
            downloadAction: makeDemographicsDownloadGet(
                props.albumId,
                props.trackId,
                props.artistId,
                from,
                to,
                "gender",
            ),
        } as GenderPieChartStateProps;
    };
};

const ConnectedGenderPieChart = compose<Props, MusicIdProps>(
    injectIntl,
    connect(makeMapStateToProps, null),
)(GenderPieChart);

export {
    GenderPieChart as GenderPieChartPresenter,
    ConnectedGenderPieChart as GenderPieChart,
};
